/**
 * ErrorHandler Service - Zustand Store
 * Single Responsibility: Handle and log errors
 */

import { create } from 'zustand';
import type { DataBindingError } from '../types/index';

export interface ErrorHandlerStore {
  lastError: DataBindingError | null;

  /**
   * Handle an error with appropriate recovery
   */
  handle: (error: DataBindingError) => void;

  /**
   * Log error details
   */
  log: (error: DataBindingError) => void;

  /**
   * Check if error is recoverable
   */
  isRecoverable: (error: DataBindingError) => boolean;

  /**
   * Get last error
   */
  getLastError: () => DataBindingError | null;

  /**
   * Clear error
   */
  clearError: () => void;
}

export const useErrorHandlerStore = create<ErrorHandlerStore>((set, get) => ({
  lastError: null as DataBindingError | null,

  handle: (error: DataBindingError): void => {
    get().log(error);
    set({ lastError: error });

    switch (error.code) {
      case 'CIRCULAR_REFERENCE':
        console.error(
          `Circular reference detected: ${(error.context?.path as string[] | undefined)?.join(' -> ')}`,
          error.context
        );
        break;
      case 'SYNC_FAILED':
        console.warn(
          `Sync failed for form ${error.context?.formID}`,
          error.context
        );
        break;
      case 'SAVE_FAILED':
        console.error(
          `Save failed for form ${error.context?.formID}`,
          error.context
        );
        break;
      case 'VALIDATION_FAILED':
        console.warn(`Validation failed`, error.context);
        break;
      case 'PATH_NOT_FOUND':
        console.error(
          `Path not found: ${error.context?.path}`,
          error.context
        );
        break;
      default:
        console.error(`Unknown error: ${error.message}`, error.context);
    }
  },

  log: (error: DataBindingError): void => {
    const timestamp = new Date().toISOString();
    console.log(
      `[${timestamp}] [${error.code}] ${error.message}`,
      error.context
    );
  },

  isRecoverable: (error: DataBindingError): boolean => {
    const recoverableCodes: DataBindingError['code'][] = [
      'SYNC_FAILED',
      'SAVE_FAILED',
      'CONCURRENT_MODIFICATION',
    ];
    return recoverableCodes.includes(error.code);
  },

  getLastError: (): DataBindingError | null => {
    return (get() as any).lastError;
  },

  clearError: (): void => {
    set({ lastError: null } as any);
  },
}));
